(() => {
  const FILTERS = {
    productType: 'filter.p.product_type',
    compatibleSystems: 'filter.p.m.custom.compatible_systems',
    connectionSize: 'filter.p.m.custom.connection_size',
    connectionType: 'filter.p.m.custom.connection_type',
    fittingStandard: 'filter.p.m.custom.fitting_standard',
    sparePartType: 'filter.p.m.custom.spare_part_type',
    nozzleStyle: 'filter.p.m.custom.nozzle_style',
    neckType: 'filter.p.m.custom.neck_type',
    neckAngle: 'filter.p.m.custom.neck_angle',
  };

  const SYSTEMS = ['Panther', 'Predator', 'Phantom', 'Shadow', 'GVS 36', 'GVS 3K'];
  const option = (value, label = value, filters = null) => ({ value, label, filters });

  const machineQuestion = {
    id: 'system',
    label: 'Which machine or system are you working on?',
    help: 'Choose the named system only when you are confident of the machine family.',
    type: 'radio',
    required: true,
    options: SYSTEMS.map((value) => option(value)),
    filterKey: FILTERS.compatibleSystems,
    summaryLabel: 'Compatible system',
  };

  const BRANCHES = {
    filters_cages: {
      fixedFilters: { [FILTERS.productType]: ['Gutter Vacuum Spare'] },
      questions: [
        machineQuestion,
        {
          id: 'part_type',
          label: 'Which filter or cage part do you need?',
          help: 'Choose a specific item, or show all filter and cage parts verified for the selected system.',
          type: 'select',
          required: true,
          summaryLabel: 'Part type',
          options: [
            option('all', 'All filters & cages', {
              [FILTERS.sparePartType]: ['Filter', 'Filter Retaining Cage', 'HEPA Deflector Ring', 'Float Valve Cup', 'Float Valve Housing'],
            }),
            option('filter', 'Filter', { [FILTERS.sparePartType]: ['Filter'] }),
            option('retaining-cage', 'Filter Retaining Cage', { [FILTERS.sparePartType]: ['Filter Retaining Cage'] }),
            option('deflector-ring', 'HEPA Deflector Ring', { [FILTERS.sparePartType]: ['HEPA Deflector Ring'] }),
            option('float-cup', 'Float Valve Cup', { [FILTERS.sparePartType]: ['Float Valve Cup'] }),
            option('float-housing', 'Float Valve Housing', { [FILTERS.sparePartType]: ['Float Valve Housing'] }),
          ],
        },
      ],
      checklist: [
        'Confirm the exact machine family from the product label or original order.',
        'Compare the filter, cage or float-valve shape with the product images.',
        'Check any wet-use, HEPA or retaining-cage notes on the product page.',
      ],
    },
    motors_electrical: {
      fixedFilters: { [FILTERS.productType]: ['Gutter Vacuum Spare'] },
      questions: [
        machineQuestion,
        {
          id: 'part_type',
          label: 'Which electrical part do you need?',
          type: 'radio',
          required: true,
          summaryLabel: 'Part type',
          options: [
            option('all', 'All electrical parts', { [FILTERS.sparePartType]: ['Motor', 'Power Switch'] }),
            option('motor', 'Motor', { [FILTERS.sparePartType]: ['Motor'] }),
            option('switch', 'Power Switch', { [FILTERS.sparePartType]: ['Power Switch'] }),
          ],
        },
      ],
      checklist: [
        'Confirm the machine family before ordering an electrical spare.',
        'Check the voltage and motor rating shown on the current component and product page.',
        'If the label or wiring differs, send GVS photos before ordering.',
      ],
    },
    machine_body: {
      fixedFilters: { [FILTERS.productType]: ['Gutter Vacuum Spare'] },
      questions: [
        {
          ...machineQuestion,
          required: false,
          options: [option('', 'Not sure / show all systems'), ...SYSTEMS.map((value) => option(value))],
        },
        {
          id: 'part_type',
          label: 'Which body or trolley part do you need?',
          type: 'select',
          required: true,
          summaryLabel: 'Part type',
          options: [
            option('all', 'All body & trolley parts', {
              [FILTERS.sparePartType]: ['Machine Head Cover', 'Trolley', 'Castor', 'Wheel', 'Handle', 'Hinge', 'Release Lever', 'End Cap', 'Inlet', 'Drum Extender Ring', 'Clip', 'Clamp', 'Fixings & Fasteners'],
            }),
            option('head-cover', 'Machine Head Cover', { [FILTERS.sparePartType]: ['Machine Head Cover'] }),
            option('trolley', 'Trolley', { [FILTERS.sparePartType]: ['Trolley'] }),
            option('castor', 'Castor', { [FILTERS.sparePartType]: ['Castor'] }),
            option('wheel', 'Wheel', { [FILTERS.sparePartType]: ['Wheel'] }),
            option('handle', 'Handle', { [FILTERS.sparePartType]: ['Handle'] }),
            option('hinge', 'Hinge', { [FILTERS.sparePartType]: ['Hinge'] }),
            option('release-lever', 'Release Lever', { [FILTERS.sparePartType]: ['Release Lever'] }),
            option('end-cap', 'End Cap', { [FILTERS.sparePartType]: ['End Cap'] }),
            option('inlet', 'Inlet', { [FILTERS.sparePartType]: ['Inlet'] }),
            option('extender-ring', 'Drum Extender Ring', { [FILTERS.sparePartType]: ['Drum Extender Ring'] }),
            option('clips', 'Clips', { [FILTERS.sparePartType]: ['Clip'] }),
            option('clamps', 'Clamps', { [FILTERS.sparePartType]: ['Clamp'] }),
            option('fixings', 'Fixings & Fasteners', { [FILTERS.sparePartType]: ['Fixings & Fasteners'] }),
          ],
        },
      ],
      checklist: [
        'Use the machine family when the part is machine-specific.',
        'For generic clamps, clips or fixings, compare the dimensions and attachment point.',
        'If the machine has been modified, send a clear photo of the existing part.',
      ],
    },
    hoses_fittings: {
      fixedFilters: { [FILTERS.productType]: ['Gutter Vacuum Hose & Fitting'] },
      questions: [
        {
          id: 'size',
          label: 'What connection size do you need?',
          help: 'Choose the nominal gutter-vacuum hose or fitting size shown on your current setup.',
          type: 'radio',
          required: false,
          filterKey: FILTERS.connectionSize,
          summaryLabel: 'Connection size',
          options: [option('', 'Not sure / show all sizes'), ...['38mm', '44mm', '51mm'].map((value) => option(value))],
        },
      ],
      checklist: [
        'Confirm which end of the hose or fitting you are replacing.',
        'Match the nominal diameter shown in the product details rather than assuming from machine family.',
        'Check whether a cuff, reducer, joiner or adaptor is also required.',
      ],
    },
    nozzles: {
      fixedFilters: { [FILTERS.productType]: ['Gutter Vacuum Nozzle'] },
      questions: [
        {
          id: 'size',
          label: 'What connection size do you need?',
          type: 'radio',
          required: true,
          filterKey: FILTERS.connectionSize,
          summaryLabel: 'Connection size',
          options: ['44mm', '46mm', '51mm'].map((value) => option(value)),
        },
        {
          id: 'style',
          label: 'Which nozzle style are you looking for?',
          help: 'You can leave this broad if you want to compare several nozzle shapes.',
          type: 'select',
          required: false,
          filterKey: FILTERS.nozzleStyle,
          summaryLabel: 'Nozzle style',
          options: [
            option('', 'Show all nozzle styles'),
            ...['Gulper', 'Crevice', 'Dirtbreaker', 'Double Claw', 'Single Claw', 'Double Ender', 'Standard', 'Front Airflow', 'Side Airflow', 'Down-Pick', 'Fin Airflow'].map((value) => option(value)),
          ],
        },
      ],
      checklist: [
        'Match the nozzle connection size to the neck or adaptor it will attach to.',
        'Choose nozzle style for the gutter profile and debris you normally encounter.',
        'Check product-page fitting notes where a nozzle uses an adaptor or special connection.',
      ],
    },
    necks: {
      fixedFilters: { [FILTERS.productType]: ['Gutter Vacuum Neck'] },
      questions: [
        {
          id: 'size',
          label: 'What connection size do you need?',
          type: 'radio',
          required: true,
          filterKey: FILTERS.connectionSize,
          summaryLabel: 'Connection size',
          options: ['44mm', '51mm'].map((value) => option(value)),
        },
        {
          id: 'neck_type',
          label: 'Do you need a rigid or flexi neck?',
          type: 'radio',
          required: false,
          filterKey: FILTERS.neckType,
          summaryLabel: 'Neck type',
          options: [option('', 'Either type'), option('Rigid'), option('Flexi')],
        },
        {
          id: 'angle',
          label: 'Which neck angle do you need?',
          type: 'select',
          required: false,
          filterKey: FILTERS.neckAngle,
          summaryLabel: 'Neck angle',
          options: [option('', 'Any angle'), ...['45°', '65°', '90°', '135°', '180°', 'Straight'].map((value) => option(value))],
        },
      ],
      checklist: [
        'Confirm the pole/nozzle connection size first.',
        'Choose rigid or flexi based on the working setup you already use.',
        'Compare the angle and overall neck shape before ordering a replacement.',
      ],
    },
    poles_clamps: {
      fixedFilters: {},
      questions: [
        {
          id: 'pole_kind',
          label: 'What are you looking for?',
          type: 'radio',
          required: true,
          summaryLabel: 'Equipment Type',
          options: [
            option('pole', 'Complete pole', { [FILTERS.productType]: ['Gutter Vacuum Pole'] }),
            option('kit', 'Pole kit', { [FILTERS.productType]: ['Gutter Vacuum Pole Kit'] }),
            option('section', 'Pole section', { [FILTERS.productType]: ['Gutter Vacuum Spare'], [FILTERS.sparePartType]: ['Pole Section'] }),
            option('clamp', 'Clamp', { [FILTERS.productType]: ['Gutter Vacuum Spare'], [FILTERS.sparePartType]: ['Clamp'] }),
          ],
        },
        {
          id: 'size',
          label: 'What connection size do you know?',
          type: 'radio',
          required: false,
          filterKey: FILTERS.connectionSize,
          summaryLabel: 'Connection size',
          options: [option('', 'Not sure / any size'), ...['38mm', '44mm', '51mm'].map((value) => option(value))],
        },
        {
          id: 'connection',
          label: 'Which connection method do you use?',
          type: 'radio',
          required: false,
          filterKey: FILTERS.connectionType,
          summaryLabel: 'Connection type',
          options: [option('', 'Not sure / either'), option('Clamped'), option('Push Fit')],
        },
      ],
      checklist: [
        'Confirm whether you need a complete pole, a kit, a replacement section or only a clamp.',
        'Match both the diameter and connection method where those details are known.',
        'Do not assume one pole section fits another range solely because the length looks similar.',
      ],
    },
    camera_parts: {
      fixedFilters: {},
      questions: [
        {
          id: 'camera_kind',
          label: 'What are you looking for?',
          type: 'radio',
          required: true,
          summaryLabel: 'Equipment Type',
          options: [
            option('accessory', 'Camera accessory or spare', { [FILTERS.productType]: ['Camera Accessory'] }),
            option('system', 'Complete camera system', { [FILTERS.productType]: ['Camera System'] }),
          ],
        },
        {
          id: 'size',
          label: 'Do you know the mounting or pole size?',
          help: 'Leave this broad for batteries, chargers, cases and other model-led accessories.',
          type: 'radio',
          required: false,
          filterKey: FILTERS.connectionSize,
          summaryLabel: 'Mounting size',
          options: [option('', 'Not size-specific / not sure'), ...['44mm', '46mm', '51mm'].map((value) => option(value))],
        },
      ],
      checklist: [
        'For batteries, chargers and cases, identify the camera kit or model rather than relying on pole diameter.',
        'For brackets and nozzle holders, confirm the mounting diameter.',
        'Compare connectors and charging details before ordering electrical accessories.',
      ],
    },
    scraper_parts: {
      fixedFilters: {},
      questions: [
        {
          id: 'scraper_kind',
          label: 'What are you trying to match?',
          type: 'radio',
          required: true,
          summaryLabel: 'Scraper part',
          options: [
            option('heads', 'Heads, blades & sets', { [FILTERS.productType]: ['Roof Cleaning Equipment', 'Roof Cleaning Accessory'] }),
            option('threaded', 'Threaded brushes & adaptors', { [FILTERS.productType]: ['Roof Cleaning Accessory'], [FILTERS.connectionType]: ['Threaded'] }),
          ],
        },
        {
          id: 'fitting_standard',
          label: 'Which threaded fitting standard do you use?',
          type: 'radio',
          required: false,
          filterKey: FILTERS.fittingStandard,
          summaryLabel: 'Fitting standard',
          showIf: { id: 'scraper_kind', values: ['threaded'] },
          options: [option('', 'Not sure / show both'), option('ACME Threaded'), option('EU Threaded')],
        },
      ],
      checklist: [
        'For ordinary scraper heads and blades, compare the head style and attachment shown on the product page.',
        'For threaded brushes and adaptors, confirm ACME or EU threaded before ordering.',
        'Do not mix threaded standards because the products appear visually similar.',
      ],
    },
    cyclonic_upgrades: {
      fixedFilters: { [FILTERS.productType]: ['Gutter Vacuum Upgrade Kit'] },
      questions: [
        {
          id: 'size',
          label: 'Which hose or inlet size are you working with?',
          type: 'radio',
          required: false,
          filterKey: FILTERS.connectionSize,
          summaryLabel: 'Connection size',
          options: [option('', 'Not sure / show all kits'), ...['44mm', '51mm', '72mm'].map((value) => option(value))],
        },
      ],
      checklist: [
        'Check the drum or body style as well as the hose size.',
        'Confirm inlet orientation and any reducers included with the kit.',
        'If your machine has been modified, send GVS photos before ordering the upgrade.',
      ],
    },
  };

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  }[char]));

  const getVisibleQuestions = (branch, answers) => branch.questions.filter((question) => {
    if (!question.showIf) return true;
    return question.showIf.values.includes(answers[question.showIf.id] || '');
  });

  const getSelectedOption = (question, value) => question.options.find((item) => item.value === value);

  const mergeFilters = (target, source) => {
    if (!source) return;
    Object.entries(source).forEach(([key, rawValues]) => {
      const values = Array.isArray(rawValues) ? rawValues : [rawValues];
      if (!target[key]) target[key] = [];
      values.filter(Boolean).forEach((value) => {
        if (!target[key].includes(value)) target[key].push(value);
      });
    });
  };

  const buildUrl = (baseUrl, filters) => {
    const url = new URL(baseUrl || '/collections/all', window.location.origin);
    const params = new URLSearchParams(url.search);
    Object.entries(filters).forEach(([key, values]) => {
      values.forEach((value) => params.append(key, value));
    });
    url.search = params.toString();
    return `${url.pathname}${url.search}${url.hash}`;
  };

  const preferredScrollBehavior = () => (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');

  const renderFinder = (root) => {
    if (root.dataset.gvsUpfReady === 'true') return;
    root.dataset.gvsUpfReady = 'true';
    const branchButtons = [...root.querySelectorAll('[data-gvs-upf-branch]')];
    const workspace = root.querySelector('[data-gvs-upf-workspace]');
    const questionsWrap = root.querySelector('[data-gvs-upf-questions]');
    const selectedBranch = root.querySelector('[data-gvs-upf-selected-branch]');
    const summary = root.querySelector('[data-gvs-upf-summary]');
    const checklist = root.querySelector('[data-gvs-upf-checklist]');
    const resultsLink = root.querySelector('[data-gvs-upf-results-link]');
    const status = root.querySelector('[data-gvs-upf-status]');
    const workspaceHeading = root.querySelector('[data-gvs-upf-workspace-heading]');
    const changeBranch = root.querySelector('[data-gvs-upf-change-branch]');
    const resetButton = root.querySelector('[data-gvs-upf-reset]');
    const progress = [...root.querySelectorAll('[data-gvs-upf-progress]')];

    if (!branchButtons.length || !workspace || !questionsWrap || !summary || !resultsLink) return;

    let currentButton = null;
    let currentBranch = null;
    let answers = {};

    const updateProgress = (step) => {
      progress.forEach((item) => {
        const itemStep = Number(item.dataset.gvsUpfProgress);
        const isActive = itemStep === step;
        item.classList.toggle('is-active', isActive);
        item.classList.toggle('is-complete', itemStep < step);
        if (isActive) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
    };

    const renderQuestions = () => {
      if (!currentBranch) return;
      const visibleQuestions = getVisibleQuestions(currentBranch, answers);
      questionsWrap.innerHTML = '';

      visibleQuestions.forEach((question) => {
        if (question.type === 'select') {
          const wrapper = document.createElement('div');
          wrapper.className = 'gvs-upf__question';
          const id = `gvs-upf-${question.id}-${Math.random().toString(36).slice(2, 8)}`;
          const label = document.createElement('label');
          label.className = 'gvs-upf__field-label';
          label.htmlFor = id;
          label.textContent = `${question.label}${question.required ? ' *' : ''}`;
          wrapper.append(label);
          if (question.help) {
            const help = document.createElement('span');
            help.className = 'gvs-upf__question-help';
            help.textContent = question.help;
            wrapper.append(help);
          }
          const select = document.createElement('select');
          select.id = id;
          select.className = 'gvs-upf__select';
          select.dataset.questionId = question.id;
          if (question.required && !answers[question.id]) {
            const placeholder = document.createElement('option');
            placeholder.value = '';
            placeholder.textContent = 'Choose an option';
            placeholder.disabled = true;
            placeholder.selected = true;
            select.append(placeholder);
          }
          question.options.forEach((item) => {
            const opt = document.createElement('option');
            opt.value = item.value;
            opt.textContent = item.label;
            if (answers[question.id] === item.value) opt.selected = true;
            select.append(opt);
          });
          select.addEventListener('change', () => handleAnswer(question.id, select.value));
          wrapper.append(select);
          questionsWrap.append(wrapper);
          return;
        }

        const fieldset = document.createElement('fieldset');
        fieldset.className = 'gvs-upf__question';
        const legend = document.createElement('legend');
        legend.textContent = `${question.label}${question.required ? ' *' : ''}`;
        fieldset.append(legend);
        if (question.help) {
          const help = document.createElement('span');
          help.className = 'gvs-upf__question-help';
          help.textContent = question.help;
          fieldset.append(help);
        }
        const choices = document.createElement('div');
        choices.className = 'gvs-upf__choices';
        question.options.forEach((item, index) => {
          const label = document.createElement('label');
          label.className = 'gvs-upf__choice';
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = `gvs-upf-${question.id}-${root.id || 'finder'}`;
          input.dataset.questionId = question.id;
          input.value = item.value;
          input.checked = answers[question.id] === item.value;
          input.addEventListener('change', () => handleAnswer(question.id, item.value));
          const span = document.createElement('span');
          span.textContent = item.label;
          label.append(input, span);
          choices.append(label);
        });
        fieldset.append(choices);
        questionsWrap.append(fieldset);
      });
    };

    const updateSummary = () => {
      if (!currentBranch || !currentButton) return;
      const visibleQuestions = getVisibleQuestions(currentBranch, answers);
      const missingRequired = visibleQuestions.some((question) => question.required && !answers[question.id]);
      const filters = {};
      mergeFilters(filters, currentBranch.fixedFilters);

      summary.innerHTML = '';
      const rows = [{ label: 'Part family', value: currentButton.dataset.branchTitle || 'Selected part family' }];

      visibleQuestions.forEach((question) => {
        const value = answers[question.id];
        if (value === undefined || value === null || value === '') return;
        const item = getSelectedOption(question, value);
        if (!item) return;
        if (item.filters) mergeFilters(filters, item.filters);
        else if (question.filterKey && value) mergeFilters(filters, { [question.filterKey]: [value] });
        rows.push({ label: question.summaryLabel || question.label, value: item.label });
      });

      rows.forEach((row) => {
        const wrapper = document.createElement('div');
        wrapper.className = 'gvs-upf__summary-row';
        wrapper.innerHTML = `<dt>${escapeHtml(row.label)}</dt><dd>${escapeHtml(row.value)}</dd>`;
        summary.append(wrapper);
      });

      checklist.innerHTML = `<p class="gvs-upf__checklist-title">Check before ordering</p><ul>${currentBranch.checklist.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`;

      if (missingRequired) {
        resultsLink.removeAttribute('href');
        resultsLink.classList.add('is-disabled');
        resultsLink.setAttribute('aria-disabled', 'true');
        resultsLink.tabIndex = -1;
        status.textContent = 'Complete the required choices to narrow the matching products.';
        updateProgress(2);
        return;
      }

      const route = buildUrl(currentButton.dataset.branchUrl, filters);
      resultsLink.href = route;
      resultsLink.classList.remove('is-disabled');
      resultsLink.setAttribute('aria-disabled', 'false');
      resultsLink.removeAttribute('tabindex');
      status.textContent = 'Your product list is ready. If no products appear, remove one filter or ask GVS to check.';
      updateProgress(3);
    };

    const handleAnswer = (questionId, value) => {
      answers[questionId] = value;
      if (currentBranch) {
        currentBranch.questions.forEach((question) => {
          if (question.showIf && question.showIf.id === questionId && !question.showIf.values.includes(value)) {
            delete answers[question.id];
          }
        });
      }
      renderQuestions();
      updateSummary();

      window.requestAnimationFrame(() => {
        const controls = [...questionsWrap.querySelectorAll(`[data-question-id="${questionId}"]`)];
        const selectedControl = controls.find((control) => control.value === value) || controls[0];
        selectedControl?.focus({ preventScroll: true });
      });
    };

    const selectBranch = (button) => {
      const key = button.dataset.branchKey;
      const branch = BRANCHES[key];
      if (!branch) return;
      currentButton = button;
      currentBranch = branch;
      answers = {};
      branchButtons.forEach((item) => item.setAttribute('aria-pressed', item === button ? 'true' : 'false'));
      selectedBranch.textContent = button.dataset.branchTitle || 'Selected part family';
      workspace.hidden = false;
      renderQuestions();
      updateSummary();
      updateProgress(2);
      window.requestAnimationFrame(() => workspaceHeading?.focus({ preventScroll: true }));
      workspace.scrollIntoView({ behavior: preferredScrollBehavior(), block: 'start' });
    };

    const resetFinder = () => {
      currentButton = null;
      currentBranch = null;
      answers = {};
      branchButtons.forEach((item) => item.setAttribute('aria-pressed', 'false'));
      workspace.hidden = true;
      questionsWrap.innerHTML = '';
      summary.innerHTML = '';
      checklist.innerHTML = '';
      status.textContent = '';
      resultsLink.removeAttribute('href');
      resultsLink.classList.add('is-disabled');
      resultsLink.setAttribute('aria-disabled', 'true');
      resultsLink.tabIndex = -1;
      updateProgress(1);
    };

    branchButtons.forEach((button) => button.addEventListener('click', () => selectBranch(button)));
    changeBranch?.addEventListener('click', () => {
      const previousButton = currentButton;
      resetFinder();
      (previousButton || branchButtons[0])?.focus();
      root.querySelector('[data-gvs-upf-step="1"]')?.scrollIntoView({ behavior: preferredScrollBehavior(), block: 'start' });
    });
    resetButton?.addEventListener('click', () => {
      resetFinder();
      branchButtons[0]?.focus();
      root.querySelector('[data-gvs-upf-step="1"]')?.scrollIntoView({ behavior: preferredScrollBehavior(), block: 'start' });
    });
    resultsLink.addEventListener('click', (event) => {
      if (resultsLink.getAttribute('aria-disabled') === 'true') event.preventDefault();
    });
  };

  const initWithin = (scope = document) => {
    if (scope.matches?.('[data-gvs-upf]')) renderFinder(scope);
    scope.querySelectorAll?.('[data-gvs-upf]').forEach(renderFinder);
  };

  const init = () => initWithin(document);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
})();
